import type { Metadata } from 'next';
import { type Country } from 'react-phone-number-input';

export const TERMS_ACCEPTANCE_ERROR_CODES = [
  'TERMS_ACCEPTANCE_REQUIRED',
  'TERMS_ACCEPTANCE_STALE',
];

// The class read's 404 for a one-time class whose date was taken away. The
// browser page and the server lookup both read it, so it is named once here.
export const ONE_TIME_CLASS_CANCELED = 'ONE_TIME_CLASS_CANCELED';

// A class order's refusals for a time that still exists but cannot take this
// booking, so the cart survives and the customer goes back to pick another.
// CLASS_NOT_BOOKABLE is deliberately absent: it means the class page itself is
// gone. So is RESCHEDULE_INTO_PAST, which exists only on
// PUT /orders/:id/reschedule.
export const SLOT_UNAVAILABLE_CODES = [
  'SLOT_FULL',
  'BOOKING_RULE_VIOLATION',
  'CLASS_ALREADY_STARTED',
];

// The class-time checks a class order, a waitlist join and the booking precheck
// run (backend checkOccurrenceBookable, occurrence-validity.ts), plus
// TENANT_CLOSED_ON_DATE (tenant-closure.ts), which only the order and the join
// ask. Each means the ids the client holds no longer name a bookable time,
// however the calendar showed it.
export const OCCURRENCE_UNAVAILABLE_CODES = [
  'CLASS_BLOCKED',
  'DATE_NOT_AVAILABLE',
  'INSTRUCTOR_NOT_AVAILABLE',
  'INSTRUCTOR_NOT_STAFFING',
  'OCCURRENCE_TIME_MISMATCH',
  'OVERRIDE_SLOT_NOT_ACTIVE',
  'SCHEDULE_NOT_ACTIVE',
  'SLOT_NOT_ACTIVE',
  'TENANT_CLOSED_ON_DATE',
];

// Of the codes above, the ones that say the ids themselves were replaced (a
// class move, a schedule edit), so the same time can still be on sale under
// new ids. The others say the time itself is closed: the class is blocked, or
// the studio, the date or the instructor is not available.
export const OCCURRENCE_REPLACED_CODES = [
  'OCCURRENCE_TIME_MISMATCH',
  'OVERRIDE_SLOT_NOT_ACTIVE',
  'SCHEDULE_NOT_ACTIVE',
  'SLOT_NOT_ACTIVE',
];

// The refusals that name one guest's details, which the guest form shows
// under that field (guestRefusal, helpers/guest-pass.ts).
export const GUEST_FIELD_REFUSAL_CODES = [
  'GUEST_IS_HOST',
  'GUEST_PHONE_IS_HOST',
  'GUEST_PHONE_DUPLICATE',
  'GUEST_DUPLICATE',
  'GUEST_NOT_NEW',
  'GUEST_ALREADY_BOOKED',
] as const;

export const IGNORED_ERROR_CODES: string[] = [
  'ACCOUNT_EXISTS',
  'INVALID_CREDENTIALS',
  // A signed-out visitor's session check, and any read a guest cannot hold.
  // The session check carries this code rather than the AuthError name, so it
  // must be here for captureError and before_send to keep dropping it.
  'LOGIN_REQUIRED',
  // The account screen cannot know in advance whether an account has ever
  // chosen a password, so it asks without one and lets this answer say. It is a
  // step in the flow, not a fault — and it fires for every account that
  // predates the has_chosen_password column, which is all of them.
  'CURRENT_PASSWORD_REQUIRED',
  'ACTIVE_SUBSCRIPTION_EXISTS',
  'NEW_CUSTOMERS_ONLY',
  'ONE_TIME_PURCHASE_ALREADY_BOUGHT',
  // A buy-once pack in a cart saved before the studio set it: the checkout
  // shows the refusal, which is the studio's rule, not a fault.
  'ONE_PER_CUSTOMER',
  'INVALID_API_KEY',
  'INVALID_EMAIL',
  'INVALID_OTP',
  'OTP_EXPIRED',
  'OTP_RATE_LIMITED',
  'OTP_LOCKED',
  'OTP_TOO_MANY_TRIES',
  // The account page's email change: answers the customer reads, not faults.
  'EMAIL_UNCHANGED',
  'EMAIL_TAKEN',
  'EMAIL_CONFIRM_NEEDED',
  // The backend's route limiters. The adapter already skips a 429 by status;
  // this also keeps quiet a caller that reports its own errors.
  'TOO_MANY_REQUESTS',
  'TENANT_NOT_LIVE',
  'TENANT_SUSPENDED',
  // A booking losing a race against the studio: the class stopped taking
  // bookings, or the instructor behind the time was turned off, after the page
  // loaded. Checkout answers both on the page, and the reschedule and the
  // waitlist join answer the second as a closed class time. Every retry would
  // file the same exception again.
  'CLASS_NOT_BOOKABLE',
  'INSTRUCTOR_NOT_STAFFING',
  // Plan-change refusals that are ordinary customer state rather than a fault.
  // The declines, the provider outages and the configuration faults stay
  // captured, because a studio owner needs to hear about those.
  'SUBSCRIPTION_PAUSED',
  'SUBSCRIPTION_PAST_DUE',
  'PLAN_CHANGE_ALREADY_SCHEDULED',
  'ALREADY_ON_PLAN',
  // A second press of Confirm is a step in the flow; the backend's own warning
  // carries the signal about overlapping confirms.
  'PLAN_CHANGE_ALREADY_APPLIED',
  // A money gift card that cannot pay for this booking or purchase: the
  // card's state, what is left on it or what it lists, which the code field
  // says in words.
  'GIFT_CARD_REDEEM_ON_GIFT_PAGE',
  'GIFT_CARD_DEACTIVATED',
  'GIFT_CARD_EXPIRED',
  'GIFT_CARD_USED_UP',
  'GIFT_CARD_BALANCE_IN_USE',
  'GIFT_CARD_CLASS_NOT_COVERED',
  'GIFT_CARD_PRODUCT_NOT_COVERED',
  'GIFT_CARD_BELOW_MINIMUM_CHARGE',
  // A kept gifted checkout the backend will not replace (its session expired,
  // or an earlier replace closed it): the checkout sends the same create again
  // without the pair, so the refusal is a step, not a fault.
  'CHECKOUT_NOT_REPLACEABLE',
  // Waitlist refusals are the customer's own state (the list filled, a rival
  // took the spot, the offer or link ran out), and each screen answers them
  // in place. OCCURRENCE_NOT_FOUND stays captured: it can mean the client sent
  // the wrong slot ids.
  'WAITLIST_FULL',
  'WAITLIST_CLOSED',
  'WAITLIST_DISABLED',
  'WAITLIST_NO_CREDIT',
  'CLASS_NOT_FULL',
  'ALREADY_WAITLISTED',
  'ALREADY_BOOKED',
  'CREDIT_EXPIRES_BEFORE_CLASS',
  'CREDIT_SOURCE_LOST',
  'SPOT_TAKEN',
  'OFFER_EXPIRED',
  'ENTRY_CLOSED',
  'CLAIM_LINK_EXPIRED',
  // A guest a host brings is refused for the host's own state (no passes or
  // spots left, an email the rules turn down), and checkout answers it inline.
  'GUEST_PASS_NOT_AVAILABLE',
  'GUEST_PASS_NONE_LEFT',
  'GUEST_NO_SPOTS',
  ...GUEST_FIELD_REFUSAL_CODES,
  // The booking page's guest actions racing another tab or the clock: the
  // guest already gone, or the host's spot no longer there to add to.
  'GUEST_HOST_NOT_BOOKED',
  'GUEST_NOT_FOUND',
  'GUEST_ALREADY_CANCELLED',
  ...TERMS_ACCEPTANCE_ERROR_CODES,
];

// 401/403 are already handled by the response interceptor upstream; they stay
// listed as a safety net because services can call handleError directly, and
// dropping them would start shipping auth failures to PostHog as API faults.
// 404s are stale links and 429s are a visitor hitting a rate limit.
export const IGNORED_ERROR_STATUSES = [401, 403, 404, 429];

// The adapter's fallback when the backend sends no message, and the same
// fallback every caller renders when it reads `.message` off a non-adapter
// error — one string so the two can never drift into different wording.
export const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

// PROVIDER_NOT_CONFIGURED is terminal: the studio has no payment provider the
// backend can reach, and nothing the customer does from here changes that. Its
// own sentence is written for an operator, so it is replaced rather than shown.
export const PAYMENT_UNAVAILABLE_COPY =
  "This studio can't take payments right now. Please contact the studio directly.";

// Distinct from the line above: the studio is fine, this one subscription has
// no Stripe customer behind it, so there is nothing on file to replace.
export const CARD_UPDATE_NO_SAVED_CARD_COPY =
  "There's no saved card on this membership to update. Please contact the studio directly.";

// Mirrors the backend's MAX_ORDER_QUANTITY: the scheduler order schemas reject
// any quantity above this, so a higher value only fails at submit.
export const MAX_ORDER_QUANTITY = 10;

// Every gift card order this tab created, as { order_id: receipt_token }, the
// latest last. The receipt sends a token beside the session id: only that
// proof (or the buyer's cookie) shows a send-to-self code, because the landing
// URL hands the session id to every analytics tag. Keyed by order, not by
// session: the hosted redirect leaves before any session id is known, and a
// token for another order proves nothing, so the code would silently go.
export const GIFT_CARD_RECEIPT_TOKENS_KEY = 'giftCardReceiptTokens';

// A class order a money gift card pays toward on the hosted page, kept for the
// page the way back from Stripe opens, which names no order: until that
// order's session expires it holds the card's share. Its id and receipt
// token, never the code. Packs and subscriptions add their type to the key.
export const GIFT_SPEND_HOSTED_ORDER_KEY = 'giftSpendHostedOrder';

export const PREVIEW_TOKEN_KEY = 'preview_token';

// The marker a studio's website adds to booking-page links (the Website links
// code in the admin's Meta Pixel drawer). The head script in app/layout.tsx
// moves it out of the URL into this window property for WebsiteLinkSignal.
export const WEBSITE_LINK_MARKER_KEY = 'tt_link';
export const WEBSITE_LINK_MARKER_GLOBAL = '__websiteLinkMarker';

// Where the head script in app/layout.tsx keeps the `token` query parameter
// it moves out of a claim link and a password-reset link.
export const CLAIM_TOKEN_KEY = 'claim_token';
export const RESET_TOKEN_KEY = 'reset_password_token';

// Distinct from PREVIEW_TOKEN_KEY: that names the query parameter and the
// sessionStorage entry, this names the header both HTTP clients send it on.
export const PREVIEW_TOKEN_HEADER = 'x-preview-token';

// The token arrives percent-decoded from the query string and is then set as a
// header value, which rejects control characters — `?preview_token=a%0Ab`
// throws inside the middleware and answers 500 for the page. Checked against
// the alphabet a token can actually carry (base64url, hex, JWT dots) rather
// than caught, so a malformed value means "no preview" instead of no page.
export const isPreviewToken = (
  value: string | null | undefined
): value is string => !!value && /^[\w.~-]{1,512}$/.test(value);

// The pathname alone, never the query string: this feeds the canonical and OG
// URL in the root layout, where a query string would publish one page per
// combination of parameters as a distinct canonical.
export const PATHNAME_HEADER = 'x-pathname';

// The query string on its own header, so the canonical above stays query-free
// while a redirect the server answers with still carries the query along.
export const SEARCH_HEADER = 'x-search';

// Deliberately matches no route: rendering the not-found page is the only way
// a middleware can answer 404 with the app's own UI. Two segments, so the
// /[slug] top-up catch-all cannot claim it.
export const UNKNOWN_HOST_PATH = '/_platform/unknown-host';

// The root layout gives every page the studio's social card, and opts the
// indexable pages into search with a canonical. Metadata merges field by
// field, so a segment kept out of search and out of link previews must clear
// robots, the canonical and both cards explicitly, or it inherits whatever the
// root layout set for its path and unfurls the studio's card.
export const NO_INDEX_METADATA: Metadata = {
  robots: { index: false, follow: false },
  alternates: null,
  openGraph: null,
  twitter: null,
};

export const EMPTY_CALENDAR_ILLUSTRATION = 'calendar-3d';

export const TIMITOMI_URL = 'https://timitomi.com';
export const TIMITOMI_APP_URL = 'https://app.timitomi.com';

// Copies ALLOWED_COUNTRIES in mokoboko-admin src/lib/phone.ts. A studio can
// sign up in any market the admin offers, and its customers need their own
// country here to book.
export const ALLOWED_COUNTRIES: Country[] = [
  // Non-European
  'US', 'CA', 'IN',
  'AE', 'AU', 'BR', 'GI', 'HK', 'JP', 'MX', 'MY', 'NZ', 'SG', 'TH',
  // European
  'AL', 'AD', 'AT', 'BY', 'BE', 'BA', 'BG', 'HR', 'CY', 'CZ',
  'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IS', 'IE', 'IT',
  'LV', 'LI', 'LT', 'LU', 'MT', 'MD', 'MC', 'ME', 'NL', 'MK',
  'NO', 'PL', 'PT', 'RO', 'RU', 'SM', 'RS', 'SK', 'SI', 'ES',
  'SE', 'CH', 'UA', 'GB',
];
